/**
 * NcHeroComponent - Section hero avec formulaire de devis rapide.
 * Gere : autocomplete produit (filtrage DOM), validation A11Y, soumission.
 */
if (!customElements.get('nc-hero-component')) {
  class NcHeroComponent extends NCComponent {
    connectedCallback() {
      const productWrap = this.$('#hero-produit-wrap');
      const productInput = this.$('#hero-produit');
      const productHidden = this.$('#hero-produit-val');

      this.#initAutocomplete(
        productWrap,
        productInput,
        this.$('#hero-produit-list'),
        productHidden
      );

      const form = this.$('#HeroDevisForm');
      if (!form) return;

      const errorEl = this.$('#HeroDevisError');
      const emailInput = this.$('#hero-email');
      const emailError = this.$('#hero-email-error');
      const productError = this.$('#hero-produit-error');

      emailInput?.addEventListener('input', () => window.NCA11y?.clearFieldError(emailInput, emailError));
      productInput?.addEventListener('input', () => window.NCA11y?.clearFieldError(productInput, productError));

      form.addEventListener('submit', e => {
        if (productWrap?.ncCommitAutocompleteValue) productWrap.ncCommitAutocompleteValue();
        else if (productHidden && productInput && !productHidden.value && productInput.value) productHidden.value = productInput.value;

        const TF = (window.NCI18n && window.NCI18n.form) || {};
        const emailValid = window.NCA11y?.validateField(emailInput, emailError, {
          required: TF.err_email_required || 'Veuillez saisir votre adresse email.',
          email: TF.err_email_format || 'Veuillez saisir une adresse email valide.'
        }) ?? true;

        const productValid = !!productHidden?.value.trim();
        if (!productValid) window.NCA11y?.setFieldError(productInput, productError, TF.err_product || 'Veuillez sélectionner au moins un produit.');
        else window.NCA11y?.clearFieldError(productInput, productError);

        if (!emailValid || !productValid) {
          e.preventDefault();
          form.querySelector('.is-error')?.focus();
          return;
        }

        const btn = form.querySelector('button[type="submit"]');
        if (errorEl) errorEl.hidden = true;
        if (btn) {
          btn.disabled = true;
          btn.textContent = (window.NCI18n && window.NCI18n.hero && window.NCI18n.hero.form_btn_sending) || 'Envoi en cours...';
        }
      });
    }

    #initAutocomplete(wrap, input, list, hidden) {
      if (!wrap || !input || !list || !hidden) return;

      let items = [];
      let origNames = [];
      const captureItems = () => {
        items = [...list.querySelectorAll('li[data-value]')];
        origNames = items.map(li => li.getAttribute('data-value'));
      };
      // Construit la liste produit depuis le JSON (helper partagé) puis recapture.
      // Idempotent (le helper ne reconstruit jamais 2×). Préchargé en idle, et
      // garanti à la 1ʳᵉ ouverture → aucun délai perceptible, DOM hors du rendu initial.
      const ensureBuilt = () => {
        if (window.ncBuildAutocompleteList) window.ncBuildAutocompleteList(list);
        captureItems();
      };
      // Données chargées en différé (/?view=ac-data) : recapture si la liste est
      // construite de façon asynchrone après le 1er ensureBuilt.
      list.addEventListener('nc:autocomplete-built', captureItems);
      captureItems();
      const selectedBox = wrap.querySelector('[data-autocomplete-selected]');
      const selected = [];
      let activeIdx = -1;

      const escHtml = s => {
        const d = document.createElement('span');
        d.textContent = s;
        return d.innerHTML;
      };
      const normalize = value => value.trim().replace(/\s+/g, ' ');
      const clearActive = () => {
        items.forEach(li => {
          li.classList.remove('is-active');
          li.setAttribute('aria-selected', 'false');
        });
        input.setAttribute('aria-activedescendant', '');
      };
      const open = () => {
        ensureBuilt();
        filter(input.value);
        wrap.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        wrap.classList.remove('is-open');
        input.setAttribute('aria-expanded', 'false');
        activeIdx = -1;
        clearActive();
      };

      const syncHidden = () => {
        hidden.value = selected.map(item => item.name + ' x ' + item.quantity).join(', ');
        items.forEach(li => {
          li.classList.toggle('is-selected', selected.some(item => item.name === li.getAttribute('data-value')));
        });
      };

      const renderSelected = () => {
        if (!selectedBox) return;
        selectedBox.innerHTML = '';
        selected.forEach(item => {
          const chip = document.createElement('span');
          chip.className = 'nc-autocomplete__chip';

          const label = document.createElement('span');
          label.className = 'nc-autocomplete__chip-label';
          label.textContent = item.name;

          const qty = document.createElement('label');
          qty.className = 'nc-autocomplete__qty';

          const qtyInput = document.createElement('input');
          qtyInput.type = 'number';
          qtyInput.className = 'nc-autocomplete__qty-input';
          qtyInput.min = '1';
          qtyInput.max = '999';
          qtyInput.step = '1';
          qtyInput.inputMode = 'numeric';
          qtyInput.value = String(item.quantity);
          const _TA = (window.NCI18n && window.NCI18n.autocomplete) || {};
          qtyInput.setAttribute('aria-label', (_TA.qty_aria || 'Quantité pour %s').replace('%s', item.name));
          qtyInput.addEventListener('input', () => {
            const nextQuantity = parseInt(qtyInput.value, 10);
            item.quantity = Number.isFinite(nextQuantity) ? Math.min(Math.max(nextQuantity, 1), 999) : 1;
            syncHidden();
          });
          qtyInput.addEventListener('blur', () => {
            qtyInput.value = String(item.quantity);
          });
          qty.appendChild(qtyInput);

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'nc-autocomplete__chip-remove';
          remove.textContent = 'x';
          const _TA2 = (window.NCI18n && window.NCI18n.autocomplete) || {};
          remove.setAttribute('aria-label', (_TA2.remove_aria || 'Retirer %s').replace('%s', item.name));
          remove.addEventListener('click', () => {
            const index = selected.findIndex(selectedItem => selectedItem.name === item.name);
            if (index >= 0) selected.splice(index, 1);
            syncHidden();
            renderSelected();
            filter(input.value);
            input.focus();
          });

          chip.append(qty, label, remove);
          selectedBox.appendChild(chip);
        });
      };

      const addValue = value => {
        value = normalize(value);
        if (!value) return;
        if (!selected.some(item => item.name === value)) selected.push({ name: value, quantity: 1 });
        input.value = '';
        window.NCA11y?.clearFieldError(input, document.getElementById(input.id + '-error'));
        syncHidden();
        renderSelected();
        filter('');
        close();
      };

      const commitInput = () => {
        if (input.value.trim()) addValue(input.value);
        else syncHidden();
      };

      wrap.ncCommitAutocompleteValue = commitInput;
      wrap.ncSetAutocompleteValues = values => {
        ensureBuilt();
        selected.splice(0, selected.length);
        values.map(normalize).filter(Boolean).forEach(value => {
          if (!selected.some(item => item.name === value)) selected.push({ name: value, quantity: 1 });
        });
        input.value = '';
        syncHidden();
        renderSelected();
        filter('');
        close();
      };

      const highlight = visible => {
        clearActive();
        if (!visible[activeIdx]) return;
        visible[activeIdx].classList.add('is-active');
        visible[activeIdx].setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', visible[activeIdx].id || '');
        visible[activeIdx].scrollIntoView({ block: 'nearest' });
      };

      const selectItem = li => addValue(li.getAttribute('data-value'));

      const filter = q => {
        q = q.toLowerCase().trim();
        list.querySelector('.nc-autocomplete__empty')?.remove();
        let hasVisible = false;

        items.forEach((li, i) => {
          const text = origNames[i].toLowerCase();
          const nameEl = li.querySelector('.nc-autocomplete__name');
          if (!nameEl) return;

          if (selected.some(item => item.name === origNames[i])) {
            li.classList.add('is-hidden');
          } else if (!q || text.includes(q)) {
            li.classList.remove('is-hidden');
            if (q) {
              const idx = text.indexOf(q);
              const orig = origNames[i];
              nameEl.innerHTML = escHtml(orig.slice(0, idx)) + '<mark>' + escHtml(orig.slice(idx, idx + q.length)) + '</mark>' + escHtml(orig.slice(idx + q.length));
            } else {
              nameEl.textContent = origNames[i];
            }
            hasVisible = true;
          } else {
            li.classList.add('is-hidden');
          }
        });

        if (!hasVisible && q) {
          const noResult = document.createElement('li');
          noResult.className = 'nc-autocomplete__empty';
          noResult.setAttribute('role', 'option');
          noResult.setAttribute('aria-disabled', 'true');
          const _TA3 = (window.NCI18n && window.NCI18n.autocomplete) || {};
          noResult.textContent = (_TA3.no_result || 'Aucun produit trouvé pour « %s »').replace('%s', q);
          list.appendChild(noResult);
        }
        activeIdx = -1;
        clearActive();
        wrap.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
      };

      input.addEventListener('focus', open);
      input.addEventListener('click', open);
      input.addEventListener('input', () => filter(input.value));
      input.addEventListener('keydown', e => {
        const visible = [...list.querySelectorAll('li[data-value]:not(.is-hidden)')];
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          activeIdx = Math.min(activeIdx + 1, visible.length - 1);
          highlight(visible);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          activeIdx = Math.max(activeIdx - 1, 0);
          highlight(visible);
        } else if (e.key === 'Enter' && activeIdx >= 0 && visible[activeIdx]) {
          e.preventDefault();
          selectItem(visible[activeIdx]);
        } else if (e.key === 'Enter' && input.value.trim()) {
          e.preventDefault();
          commitInput();
        } else if (e.key === 'Escape') {
          close();
        }
      });

      document.addEventListener('click', e => {
        if (!wrap.contains(e.target)) close();
      });
      list.addEventListener('click', e => {
        const li = e.target.closest('li[data-value]');
        if (li) selectItem(li);
      });

      // Préchargement hors chemin critique : construit la liste pendant un temps mort.
      if ('requestIdleCallback' in window) requestIdleCallback(ensureBuilt, { timeout: 2500 });
      else setTimeout(ensureBuilt, 1200);
    }
  }

  customElements.define('nc-hero-component', NcHeroComponent);
}
